import React, { useState, useEffect, useMemo } from 'react';
import { usersAPI, playersAPI, workspacesAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Workspace } from '@/types';
import { UserItem } from './types';

export function useUsersPage() {
  const { user: currentUser, startImpersonation } = useAuth();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allPlayers, setAllPlayers] = useState<{id: string; name: string}[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const adminCount = users.filter((u) => u.role === 'admin').length;

  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(25);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q),
    );
  }, [users, search]);

  // Reset progressive reveal whenever the filter changes.
  // Reset progressive reveal and clear selection when the filter changes, so
  // rows hidden by the search can't be silently bulk-deleted.
  useEffect(() => { setVisibleCount(25); setSelectedIds(new Set()); }, [search]);

  const visibleUsers = useMemo(() => filteredUsers.slice(0, visibleCount), [filteredUsers, visibleCount]);
  const hasMore = visibleCount < filteredUsers.length;
  const loadMore = () => setVisibleCount((c) => Math.min(c + 25, filteredUsers.length));

  const selectableIds = useMemo(
    () => filteredUsers.filter((u) => u.id !== currentUser?.id).map((u) => u.id),
    [filteredUsers, currentUser?.id],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelected = (id: string) => {
    if (id === currentUser?.id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelectedIds(new Set());
  const toggleSelectAll = () => {
    setSelectedIds((prev) => (selectableIds.every((id) => prev.has(id)) ? new Set() : new Set(selectableIds)));
  };

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'scout', playerId: '', workspaceIds: [] as string[] });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit form
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', playerId: '', workspaceIds: [] as string[] });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Password reset
  const [resetPasswordId, setResetPasswordId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Delete
  const [deleteUser, setDeleteUser] = useState<UserItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds).filter((id) => id !== currentUser?.id);
    if (ids.length === 0) { setBulkDeleteOpen(false); return; }
    setBulkDeleteLoading(true);
    setError('');
    const results = await Promise.allSettled(ids.map((id) => usersAPI.delete(id)));
    const failedIds = ids.filter((_, i) => results[i].status === 'rejected');
    // Keep the failed ids selected so the admin can retry; clear on full success.
    setSelectedIds(new Set(failedIds));
    setBulkDeleteOpen(false);
    // Await the refetch first — fetchUsers clears `error` on success, so the
    // partial-failure message must be set AFTER it resolves or it gets wiped.
    await fetchUsers();
    setBulkDeleteLoading(false);
    if (failedIds.length > 0) setError(`Failed to delete ${failedIds.length} of ${ids.length} selected user(s).`);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.getAll();
      setUsers(res.data);
      setError('');
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    playersAPI.getAll(undefined, 1, 999).then((res: { data: { id: string; name: string }[] }) => {
      setAllPlayers(res.data.map((p: { id: string; name: string }) => ({ id: p.id, name: p.name })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    workspacesAPI.list().then(setWorkspaces).catch(() => {});
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    // An unlinked player has no squad to inherit, so a workspace must be chosen.
    if (createForm.role === 'player' && !createForm.playerId && createForm.workspaceIds.length === 0) {
      setCreateError('A player who is not linked to a player record needs at least one workspace.');
      return;
    }
    if (createForm.role !== 'player' && createForm.workspaceIds.length === 0) {
      if (!window.confirm("This user won't be able to use the app until they're added to a workspace — continue?")) { return; }
    }
    setCreateLoading(true);
    setCreateError('');
    try {
      const payload: any = {
        name: createForm.name, email: createForm.email, password: createForm.password, role: createForm.role,
        workspaceIds: createForm.workspaceIds,
      };
      if (createForm.role === 'player' && createForm.playerId) payload.playerId = createForm.playerId;
      await usersAPI.create(payload);
      setShowCreate(false);
      setCreateForm({ name: '', email: '', password: '', role: 'scout', playerId: '', workspaceIds: [] });
      fetchUsers();
    } catch (err: any) {
      setCreateError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const startEdit = (user: UserItem) => {
    setEditingId(user.id);
    setEditForm({ name: user.name, role: user.role, playerId: (user as any).playerId || '', workspaceIds: user.workspaceIds || [] });
    setEditError('');
    // Close password reset if open
    setResetPasswordId(null);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    // An unlinked player has no squad to inherit, so a workspace must be chosen.
    if (editForm.role === 'player' && !editForm.playerId && editForm.workspaceIds.length === 0) {
      setEditError('A player who is not linked to a player record needs at least one workspace.');
      return;
    }
    if (editForm.role !== 'player' && editForm.workspaceIds.length === 0) {
      if (!window.confirm("This user won't be able to use the app until they're added to a workspace — continue?")) return;
    }
    setEditLoading(true);
    setEditError('');
    try {
      const payload: any = { name: editForm.name, role: editForm.role };
      const linkedPlayer = editForm.role === 'player' && !!editForm.playerId;
      if (linkedPlayer) payload.playerId = editForm.playerId;
      await usersAPI.update(editingId, payload);
      // A linked player's workspace is set by the backend from their player record;
      // the SetWorkspaces endpoint rejects manual edits for them.
      if (!linkedPlayer) {
        await usersAPI.setWorkspaces(editingId, editForm.workspaceIds);
      }
      setEditingId(null);
      fetchUsers();
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordId) return;
    setResetLoading(true);
    setResetError('');
    setResetSuccess('');
    try {
      await usersAPI.updatePassword(resetPasswordId, { password: newPassword });
      setResetSuccess('Password updated successfully');
      setNewPassword('');
      setTimeout(() => {
        setResetPasswordId(null);
        setResetSuccess('');
      }, 1500);
    } catch (err: any) {
      setResetError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setDeleteLoading(true);
    try {
      const deletedId = deleteUser.id;
      await usersAPI.delete(deletedId);
      setSelectedIds((prev) => {
        if (!prev.has(deletedId)) return prev;
        const next = new Set(prev);
        next.delete(deletedId);
        return next;
      });
      setDeleteUser(null);
      fetchUsers();
    } catch {
      // Modal will close, user stays
    } finally {
      setDeleteLoading(false);
    }
  };

  const [impersonateUser, setImpersonateUser] = useState<UserItem | null>(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  const confirmImpersonate = async () => {
    if (!impersonateUser) return;
    setImpersonateLoading(true);
    try {
      await startImpersonation(impersonateUser.id);
      // startImpersonation hard-reloads on success; this line is only reached on error.
    } catch {
      setError('Failed to impersonate user');
      setImpersonateUser(null);
    } finally {
      setImpersonateLoading(false);
    }
  };

  const startResetPassword = (user: UserItem) => {
    setResetPasswordId(user.id);
    setNewPassword('');
    setResetError('');
    setResetSuccess('');
    // Close edit if open
    setEditingId(null);
  };

  return {
    currentUser,
    users,
    loading,
    error,
    allPlayers,
    workspaces,
    adminCount,
    showCreate,
    setShowCreate,
    createForm,
    setCreateForm,
    createLoading,
    createError,
    setCreateError,
    editingId,
    setEditingId,
    editForm,
    setEditForm,
    editLoading,
    editError,
    resetPasswordId,
    setResetPasswordId,
    newPassword,
    setNewPassword,
    resetLoading,
    resetError,
    resetSuccess,
    deleteUser,
    setDeleteUser,
    deleteLoading,
    impersonateUser,
    setImpersonateUser,
    impersonateLoading,
    handleCreate,
    startEdit,
    handleEdit,
    handleResetPassword,
    handleDelete,
    confirmImpersonate,
    startResetPassword,
    search,
    setSearch,
    filteredUsers,
    visibleUsers,
    hasMore,
    loadMore,
    selectedIds,
    allSelected,
    someSelected,
    toggleSelected,
    clearSelected,
    toggleSelectAll,
    bulkDeleteOpen,
    setBulkDeleteOpen,
    bulkDeleteLoading,
    handleBulkDelete,
  };
}
